import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Toast from '../../shared/components/ui/Toast';
import AdminPortal from '../../features/auth/pages/AdminPortal';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import RouteRenderer from './RouteRenderer';
import '../../shared/components/layout/StoreDashboardLayout.css';
import './AppRouter.css';
import {
  isAuthBypassRoute,
  isConcreteRoute,
  isPlaceholderRoute,
  isPublicRoute,
  isStoreRoute,
  PRODUCT_DETAIL_TYPES,
  resolveRoute,
  shouldShowStoreAccountSidebar
} from './routes';
import { parseJwt } from '../../shared/utils/jwt';
import { validateStoreLogin } from '../../features/auth/utils/storeLogin';
import { validateStoreRegister } from '../../features/auth/utils/storeRegister';
import {
  AUTH_STORES_REGISTER,
  AUTH_STORES_LOGIN,
  AUTH_STORES_LOGOUT,
  PROFILE_STORES_SELF,
  PROFILE_STORES_UPDATE
} from '../../shared/constants/endpoints';
import { storage } from '../../shared/utils/storage.js';
import {
  clearAuthState,
  setCashierField,
  setRoute,
  setStoreLoginField,
  setStoreRegisterField,
  setStoreMallUuid,
  setStoreName,
  setStoreToken,
  setStoreUuid
} from '../providers/store';

const getRouteFromPath = () => {
  const path = window.location.pathname || '/';
  const search = window.location.search || '';
  const fullPath = `${path || '/'}${search}`;
  if (fullPath && !fullPath.startsWith('/')) {
    return `/${fullPath}`;
  }
  return fullPath || '/';
};

const AppRoutes = ({ request, bootstrapSession, onAuthChange, onPushActivity, sessionBootstrapComplete, sessionExpiredAt }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const route = useSelector((state) => state.route.path);
  const storeRegister = useSelector((state) => state.store.storeRegister);
  const storeLogin = useSelector((state) => state.store.storeLogin);
  const storeToken = useSelector((state) => state.store.storeToken);
  const storeUuid = useSelector((state) => state.store.storeUuid);

  const [toast, setToast] = useState(null);
  const [isInitialBootLoading, setIsInitialBootLoading] = useState(true);

  const isStoreLoggedIn = Boolean(storeToken || storeUuid);

  const navigate = useCallback((path, { replace } = {}) => {
    if (replace) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }
    window.dispatchEvent(new PopStateEvent('popstate'));
    dispatch(setRoute(path || '/'));
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialBootLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (window.location.hash && window.location.hash.startsWith('#/')) {
      const nextPath = window.location.hash.replace('#', '');
      window.history.replaceState({}, '', nextPath || '/');
    }
    const handlePopState = () => dispatch(setRoute(getRouteFromPath()));
    window.addEventListener('popstate', handlePopState);
    handlePopState();
    return () => window.removeEventListener('popstate', handlePopState);
  }, [dispatch]);

  useEffect(() => {
    const handleApiError = (event) => {
      const requestName = event?.detail?.requestName;
      const path = event?.detail?.path || '';
      const status = Number(event?.detail?.error?.status || 0);
      if (requestName === 'stores' || requestName === 'sections') {
        return;
      }
      if (path.startsWith('/v1/profile/stores') || path.startsWith('/v1/profile/sections')) {
        return;
      }
      if (path.startsWith(PROFILE_STORES_SELF) || path.startsWith(PROFILE_STORES_UPDATE(''))) {
        return;
      }
      if (path.startsWith('/v1/analytics') && (status === 400 || status === 403 || status === 422)) {
        return;
      }
      setToast({ message: 'Technical difficulties. Please try again later.', variant: 'error' });
    };

    window.addEventListener('api-error', handleApiError);
    return () => window.removeEventListener('api-error', handleApiError);
  }, []);

  useEffect(() => {
    if (route === '/login') {
      dispatch(setStoreLoginField({ field: 'email', value: '' }));
      dispatch(setStoreLoginField({ field: 'storeCode', value: '' }));
      dispatch(setStoreLoginField({ field: 'password', value: '' }));
    }

    if (route === '/register') {
      const savedLocation = (() => {
        try {
          const raw = localStorage.getItem('storeRegisterLocation');
          return raw ? JSON.parse(raw) : {};
        } catch {
          return {};
        }
      })();
      const savedDetails = (() => {
        try {
          const raw = localStorage.getItem('storeRegisterDetails');
          return raw ? JSON.parse(raw) : {};
        } catch {
          return {};
        }
      })();

      dispatch(setStoreRegisterField({ field: 'storeName', value: savedDetails.storeName || '' }));
      dispatch(setStoreRegisterField({ field: 'clientType', value: savedDetails.clientType || '' }));
      dispatch(setStoreRegisterField({ field: 'email', value: savedDetails.email || '' }));
      dispatch(setStoreRegisterField({ field: 'phone', value: savedDetails.phone || '' }));
      dispatch(setStoreRegisterField({ field: 'storeCode', value: savedDetails.storeCode || '' }));
      dispatch(setStoreRegisterField({ field: 'floorNumber', value: savedDetails.floorNumber || '' }));
      dispatch(setStoreRegisterField({ field: 'phoneType', value: savedDetails.phoneType || 'mobile' }));
      dispatch(setStoreRegisterField({ field: 'address', value: savedDetails.address || '' }));
      dispatch(setStoreRegisterField({ field: 'latitude', value: savedDetails.latitude || null }));
      dispatch(setStoreRegisterField({ field: 'longitude', value: savedDetails.longitude || null }));
      dispatch(setStoreRegisterField({ field: 'city', value: savedLocation.city || '' }));
      dispatch(setStoreRegisterField({ field: 'area', value: savedLocation.area || '' }));
      dispatch(setStoreRegisterField({ field: 'state', value: savedLocation.state || '' }));
      dispatch(setStoreRegisterField({ field: 'pincode', value: savedLocation.pincode || '' }));
      dispatch(setStoreRegisterField({ field: 'mallUuid', value: savedLocation.mallUuid || '' }));
      dispatch(setStoreRegisterField({ field: 'brandUuid', value: '' }));
      dispatch(setStoreRegisterField({ field: 'zoneWing', value: '' }));
      dispatch(setStoreRegisterField({ field: 'parkingZones', value: '' }));
      dispatch(setStoreRegisterField({ field: 'totalBillingSections', value: '' }));
      dispatch(setStoreRegisterField({ field: 'totalBillingCounters', value: '' }));
      dispatch(setStoreRegisterField({ field: 'totalTrialSections', value: '' }));
      dispatch(setStoreRegisterField({ field: 'totalTrialRooms', value: '' }));
      dispatch(setStoreRegisterField({ field: 'hasParkingIntegration', value: 'false' }));
      dispatch(setStoreRegisterField({ field: 'chargesTaxSeparately', value: false }));
      dispatch(setStoreRegisterField({ field: 'isNewMall', value: false }));
    }
  }, [route, dispatch]);

  useEffect(() => {
    if (!sessionBootstrapComplete) {
      return;
    }

    if (route === '/login' && auth.role === 'store' && isStoreLoggedIn) {
      navigate('/store', { replace: true });
      return;
    }

    if (route.startsWith('/store') && auth.role !== 'store') {
      navigate('/login', { replace: true });
      return;
    }

    if (auth.role === 'store' && !isStoreRoute(route) && !isPublicRoute(route) && !isAuthBypassRoute(route) && !route.startsWith('/admin')) {
      navigate(isConcreteRoute(route) ? '/store' : '/404', { replace: true });
      return;
    }

    if (auth.role !== 'store' && !isConcreteRoute(route) && route !== '/404') {
      navigate('/404', { replace: true });
      return;
    }

    if (auth.role !== 'store' && route.startsWith('/store')) {
      navigate('/login', { replace: true });
    }
  }, [auth.role, dispatch, isStoreLoggedIn, navigate, route, sessionBootstrapComplete]);

   useEffect(() => {
     if (!sessionExpiredAt) {
       return;
     }

     if (isAuthBypassRoute(route)) {
       return;
     }

     if (!isStoreLoggedIn && !auth.token) {
       return;
     }

     storage.purgeLegacyWebAuthStorage();
     dispatch(clearAuthState());
     dispatch(setStoreToken(''));
     dispatch(setStoreUuid(''));
     dispatch(setStoreMallUuid(''));
     dispatch(setStoreName(''));
     setToast({ message: 'Session expired. Please login again', variant: 'error' });
     navigate('/', { replace: true });
   }, [auth.token, dispatch, isStoreLoggedIn, navigate, route, sessionExpiredAt]);

  const handleStoreRegisterFieldChange = (field, value) => {
    dispatch(setStoreRegisterField({ field, value }));
  };

  const handleStoreLoginFieldChange = (field, value) => {
    dispatch(setStoreLoginField({ field, value }));
  };

  const handleStoreRegister = async () => {
    const errors = validateStoreRegister(storeRegister);
    if (errors.length) {
      setToast({ message: errors[0], variant: 'error' });
      return;
    }

	    try {
	      const data = await request(AUTH_STORES_REGISTER, {
	        method: 'POST',
	        body: JSON.stringify(storeRegister)
	      });
	      onPushActivity('Store registered', data);
	      storage.purgeLegacyWebAuthStorage();
      window.localStorage.removeItem('storeRegisterLocation');
      window.localStorage.removeItem('storeRegisterDetails');

      const blankFields = ['storeName', 'clientType', 'email', 'phone', 'storeCode', 'floorNumber', 'password', 'address', 'city', 'area', 'state', 'pincode', 'mallUuid', 'brandUuid', 'zoneWing', 'parkingZones', 'totalBillingSections', 'totalBillingCounters', 'totalTrialSections', 'totalTrialRooms'];
      blankFields.forEach((field) => dispatch(setStoreRegisterField({ field, value: '' })));
      dispatch(setStoreRegisterField({ field: 'latitude', value: null }));
      dispatch(setStoreRegisterField({ field: 'longitude', value: null }));
      dispatch(setStoreRegisterField({ field: 'phoneType', value: 'mobile' }));
      dispatch(setStoreRegisterField({ field: 'hasParkingIntegration', value: 'false' }));
      dispatch(setStoreRegisterField({ field: 'chargesTaxSeparately', value: false }));
      dispatch(setStoreRegisterField({ field: 'isNewMall', value: false }));

      dispatch(clearAuthState());
	      dispatch(setStoreToken(''));
	      dispatch(setStoreUuid(''));
      dispatch(setStoreMallUuid(''));
      navigate('/', { replace: true });
      setToast({
        message: "You'll receive an email on your registration process. Please wait for some time.",
        variant: 'success'
      });
		    } catch (error) {
		      setToast({ message: 'Error, Please try again', variant: 'error' });
		      onPushActivity('Store registration failed', error.data || { status: error.status });
		    }
		  };

  const handleStoreLogin = async () => {
    const errors = validateStoreLogin(storeLogin);
    if (errors.length) {
      setToast({ message: errors[0], variant: 'error' });
      return;
    }

    try {
      const data = await request(AUTH_STORES_LOGIN, {
        method: 'POST',
        body: JSON.stringify(storeLogin),
        requestName: 'store-login'
      });
      const hydratedSession = await bootstrapSession({ ignoreUnauthed: true });
      const hydratedStoreUuid = hydratedSession?.session?.storeUuid || '';

      if (hydratedStoreUuid) {
        dispatch(setCashierField({ field: 'storeUuid', value: hydratedStoreUuid }));
      }

      setToast({ message: 'Login successful', variant: 'success' });
      window.localStorage.removeItem('dashboardActiveTab');
      onPushActivity('Store logged in', data);
      onAuthChange({
        token: hydratedSession?.accessToken || '',
        roleHint: hydratedSession?.role || 'store'
      });
	    } catch (error) {
	      const apiMessage = error?.data?.message || error?.message || '';
	      const message = apiMessage && apiMessage !== 'Request failed'
	        ? apiMessage
	        : 'Login failed. Invalid credentials provided.';
	      setToast({ message, variant: 'error' });
	      onPushActivity('Store login failed', error.data || { status: error.status });
	    }
	  };

  const handleGoogleAuthSuccess = useCallback(async (authData = {}) => {
    const resolvedRole = String(authData?.role || authData?.accountType || '').toLowerCase().includes('store')
      ? 'store'
      : 'user';

    if (!authData) {
      setToast({ message: 'Login failed. Missing access token.', variant: 'error' });
      return;
    }

    if (resolvedRole === 'store') {
      const hydratedSession = await bootstrapSession({ ignoreUnauthed: true });
      const storeUuidValue = hydratedSession?.session?.storeUuid || '';

      if (storeUuidValue) {
        dispatch(setCashierField({ field: 'storeUuid', value: storeUuidValue }));
      }

      window.localStorage.removeItem('dashboardActiveTab');
      onPushActivity('Google store login successful', authData);
      onAuthChange({
        token: hydratedSession?.accessToken || '',
        roleHint: hydratedSession?.role || 'store'
      });
      return;
    }

    dispatch(setStoreToken(''));
    dispatch(setStoreUuid(''));
    dispatch(setStoreMallUuid(''));
    dispatch(setStoreName(''));
    onPushActivity('Google user login successful', authData);
    onAuthChange({ token: '', roleHint: 'user' });
  }, [bootstrapSession, dispatch, onAuthChange, onPushActivity]);

  const handleStoreLogout = useCallback(() => {
    (async () => {
      try {
        await request(AUTH_STORES_LOGOUT, { method: 'POST', requestName: 'store-logout' });
      } catch (error) {
        // Continue clearing local state even if the network request fails.
      }
      storage.purgeLegacyWebAuthStorage();
      dispatch(clearAuthState());
      dispatch(setStoreToken(''));
      dispatch(setStoreUuid(''));
      dispatch(setStoreMallUuid(''));
      dispatch(setStoreName(''));
      navigate('/', { replace: true });
      setToast({ message: 'Logged out successfully', variant: 'success' });
    })();
  }, [dispatch, navigate, request]);

  const handleToastClose = () => {
    setToast(null);
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  const routeDef = resolveRoute(route);
  const isAdminRoute = route.startsWith('/admin');
  const is404Route = route === '/404';
  const isNotFound = !isConcreteRoute(route);
  const shouldShowNotFound = isNotFound || is404Route;
  const showSidebar = !shouldShowNotFound && shouldShowStoreAccountSidebar(route);
  const tokenClaims = parseJwt(storeToken);
  const membershipEnabled = typeof tokenClaims?.membershipEnabled === 'boolean'
    ? tokenClaims.membershipEnabled
    : typeof tokenClaims?.membership_enabled === 'boolean'
      ? tokenClaims.membership_enabled
      : false;

  const storePath = isStoreRoute(route);
  const isStoreRootReady = route === '/store' && auth.role === 'store';
  const isStoreChildReady = route !== '/store' && auth.role === 'store' && isStoreLoggedIn;
  const isStoreRouteReady = !storePath || isStoreRootReady || isStoreChildReady;
  const shouldShowRouteBootLoader = !sessionBootstrapComplete || (!isInitialBootLoading && storePath && !isStoreRouteReady);
  const isProductDetailRoute = PRODUCT_DETAIL_TYPES.some((type) => route === `/${type}`);

  const hasResolvedRenderableRoute = Boolean(
    shouldShowRouteBootLoader
    || shouldShowNotFound
    || routeDef
    || isProductDetailRoute
    || isPlaceholderRoute(route)
  );

  if (isAdminRoute) {
    return (
      <ErrorBoundary level="app">
        <AdminPortal />
      </ErrorBoundary>
    );
  }

	  return (
	    <ErrorBoundary level="app">
	      <>
	        <RouteRenderer
          route={route}
          authRole={auth.role}
          authToken={auth.token}
          storeRegister={storeRegister}
          storeLogin={storeLogin}
          storeToken={storeToken}
          storeUuid={storeUuid}
          isStoreLoggedIn={isStoreLoggedIn}
          request={request}
          onPushActivity={onPushActivity}
          onToast={setToast}
          onNavigate={handleNavigate}
          onStoreRegisterFieldChange={handleStoreRegisterFieldChange}
          onStoreLoginFieldChange={handleStoreLoginFieldChange}
          onStoreRegister={handleStoreRegister}
          onStoreLogin={handleStoreLogin}
          onGoogleAuthSuccess={handleGoogleAuthSuccess}
          onStoreLogout={handleStoreLogout}
          showSidebar={showSidebar}
          membershipEnabled={membershipEnabled}
          shouldShowNotFound={shouldShowNotFound}
          shouldShowRouteBootLoader={shouldShowRouteBootLoader}
          isInitialBootLoading={isInitialBootLoading}
          hasResolvedRenderableRoute={hasResolvedRenderableRoute}
        />
	        <Toast
	          message={toast?.message}
	          variant={toast?.variant}
	          onClose={handleToastClose}
	        />
	      </>
	    </ErrorBoundary>
	  );
};

export default AppRoutes;
